import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faClose,
    faBoxOpen,
    faSquareCheck,
    faCaretUp,
    faCaretDown,
    faSquare,
    faCircle,
    faCaretRight,
} from '@fortawesome/free-solid-svg-icons';
import { Button } from './button';
import { Popover } from 'antd';

export const DropdownListType = Object.freeze({
    RADIO: 'radio',
    CHECKBOX: 'checkbox',
});

const Title = ({ title, handleOpenChange, showCloseButton }) => {
    return (
        <div className='min-w-38 z-50 flex justify-between items-center pb-1 border-b text-base select-none'>
            <div className='text-texts-secondary300 ml-1 mr-3'>{title}</div>
            {showCloseButton && (
                <Button
                    onClick={(e) => {
                        handleOpenChange(false);
                        e.stopPropagation();
                    }}
                    className='w-6 h-6'
                    title='Close'
                    hoverText={'Close the dropdown'}
                    icon={faClose}
                    type={'danger'}
                />
            )}
        </div>
    );
};

const ListType = ({ listType, isSelected }) => {
    if (listType === DropdownListType.CHECKBOX) {
        return (
            <FontAwesomeIcon
                icon={isSelected ? faSquareCheck : faSquare}
                className={`ml-4 border p-0.5 rounded shadow-inner ${
                    isSelected
                        ? 'text-texts-tertiary'
                        : 'text-icons-tertiary100'
                }`}
            />
        );
    }

    if (listType === DropdownListType.RADIO) {
        return (
            <FontAwesomeIcon
                icon={faCircle}
                className={`ml-4 border p-0.5 rounded-full shadow-inner ${
                    isSelected
                        ? 'text-texts-tertiary'
                        : 'text-icons-tertiary100'
                }`}
            />
        );
    }

    if (isSelected) {
        return (
            <FontAwesomeIcon
                icon={faCircle}
                className='ml-4 text-texts-tertiary border p-0.5 rounded-full shadow-inner'
            />
        );
    }
    return <></>;
};

const getTypeClass = (type) => {
    if (
        !type ||
        (type && !(typeof type === 'string' || type instanceof String))
    )
        return '';

    const classMap = {
        success: 'text-icons-primary',
        info: 'text-icons-info',
        warning: 'text-icons-warning',
        danger: 'text-icons-danger',
    };

    return classMap[type.toLowerCase()] || '';
};

const renderDropDownList = (
    dropDownList,
    handleOpenChange,
    disabled,
    listType
) => {
    const hasHeadingsInCurrentList = (list) =>
        list && list.some((sub) => sub.isHeading);

    return dropDownList.map((item, index) => {
        if (item.isSubmenu && item.items) {
            const hasHeadingsInSubmenu = hasHeadingsInCurrentList(item.items);
            const parentDisabled = item.disabled || disabled;

            return (
                <Popover
                    key={index}
                    content={
                        <div className='py-1 min-w-[170px]'>
                            {item.items.map((subItem, subIndex) => {
                                // 1. Check for Divider
                                if (subItem.isDivider) {
                                    return (
                                        <hr
                                            key={`divider-${subIndex}`}
                                            className='my-1 border-gray-200'
                                        />
                                    );
                                }

                                // 2. Check for Heading
                                if (subItem.isHeading) {
                                    return (
                                        <div
                                            key={`heading-${subIndex}`}
                                            className='px-2 pt-2 pb-0.5 text-xs font-semibold text-texts-secondary uppercase tracking-wider select-none'
                                        >
                                            {subItem.label}
                                        </div>
                                    );
                                }

                                // 3. Render regular clickable menu item
                                const itemDisabled =
                                    subItem.disabled || disabled;
                                return (
                                    <a
                                        key={subIndex}
                                        href='#ref'
                                        role='menuitem'
                                        className={`block w-full whitespace-no-wrap text-base
                                            ${
                                                itemDisabled
                                                    ? 'text-texts-secondary300 bg-background-slate cursor-not-allowed'
                                                    : 'text-texts-secondary300 hover:bg-backgrounds-primary100 cursor-pointer'
                                            }
                                            ${
                                                hasHeadingsInSubmenu
                                                    ? 'pl-4 pr-2 py-1.5'
                                                    : 'px-2 py-1.5'
                                            }`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (
                                                !itemDisabled &&
                                                subItem.action
                                            ) {
                                                subItem.action();
                                                handleOpenChange(false);
                                            }
                                        }}
                                        title={
                                            itemDisabled
                                                ? subItem.hoverTextDisabled ||
                                                  'Disabled'
                                                : subItem.hoverText ||
                                                  subItem.label
                                        }
                                    >
                                        <div className='flex items-center'>
                                            {subItem.icon && (
                                                <FontAwesomeIcon
                                                    icon={subItem.icon}
                                                    className='mr-2 text-texts-tertiary200 drop-shadow-sm'
                                                />
                                            )}
                                            <div className='max-w-60 truncate'>
                                                {subItem.label}
                                            </div>
                                        </div>
                                    </a>
                                );
                            })}
                        </div>
                    }
                    trigger='hover'
                    placement='right'
                >
                    <a
                        href='#ref'
                        role='menuitem'
                        onClick={(e) => e.preventDefault()}
                        title={
                            parentDisabled
                                ? item.hoverTextDisabled || 'Disabled'
                                : item.hoverText || item.label
                        }
                        className={`px-2 py-1.5 block w-full whitespace-no-wrap rounded-lg text-base
                            ${
                                disabled
                                    ? 'text-texts-secondary300 bg-background-slate cursor-not-allowed'
                                    : 'text-texts-secondary300 hover:bg-backgrounds-primary100'
                            }`}
                    >
                        <div className='flex items-center justify-between'>
                            <div className='flex items-center'>
                                {item.icon && (
                                    <FontAwesomeIcon
                                        icon={item.icon}
                                        className={`mr-2 ${
                                            item.disabled
                                                ? 'text-texts-secondary300'
                                                : item.type
                                                ? getTypeClass(item.type)
                                                : 'text-texts-tertiary200'
                                        } drop-shadow-sm`}
                                    />
                                )}
                                <div className='max-w-60 truncate text-base'>
                                    {item.label}
                                </div>
                            </div>
                            <FontAwesomeIcon
                                icon={faCaretRight}
                                className='ml-2'
                            />
                        </div>
                    </a>
                </Popover>
            );
        }

        const itemDisabled = item.disabled || disabled;
        return (
            <a
                href='#ref'
                key={index}
                role='menuitem'
                title={
                    itemDisabled
                        ? item.hoverTextDisabled || 'Disabled'
                        : item.hoverText || item.label
                }
                className={
                    `block w-full whitespace-no-wrap rounded-lg text-base
                    ${
                        itemDisabled
                            ? 'text-texts-secondary300 bg-background-slate cursor-not-allowed'
                            : 'text-texts-secondary300 hover:bg-backgrounds-primary100 cursor-pointer'
                    }
                    ${
                        hasHeadingsInCurrentList(dropDownList)
                            ? 'pl-4 pr-2 py-1.5'
                            : 'px-2 py-1.5'
                    }` // Indent if headings exist at top level
                }
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!itemDisabled) {
                        if (item.action) item.action();
                        if (
                            listType !== DropdownListType.CHECKBOX &&
                            listType !== DropdownListType.RADIO
                        ) {
                            handleOpenChange(false);
                        }
                    }
                }}
            >
                <div className='flex items-center justify-between'>
                    <div className='flex items-center'>
                        {item.icon && (
                            <FontAwesomeIcon
                                icon={item.icon}
                                className={`mr-2 ${
                                    itemDisabled
                                        ? 'text-texts-secondary300'
                                        : item.type
                                        ? getTypeClass(item.type)
                                        : item.iconColor
                                        ? ''
                                        : 'text-texts-tertiary200'
                                } drop-shadow-sm`}
                                style={
                                    item.iconColor && !itemDisabled
                                        ? { color: item.iconColor }
                                        : {}
                                }
                            />
                        )}
                        <div className='max-w-60 truncate text-base'>
                            {item.label}
                        </div>
                    </div>
                    <ListType listType={listType} isSelected={item.selected} />
                </div>
            </a>
        );
    });
};

const Content = ({
    dropDownList,
    handleOpenChange,
    disabled,
    listType,
    listClassName,
}) => {
    if (dropDownList && dropDownList.length) {
        return (
            <div
                className={`w-full h-fit max-h-[50vh] max-w-[70vw] overflow-auto ${
                    listClassName || ''
                }`}
                role='menu'
            >
                {renderDropDownList(
                    dropDownList,
                    handleOpenChange,
                    disabled,
                    listType
                )}
            </div>
        );
    } else {
        return (
            <div className='w-full flex flex-col py-2 justify-center items-center'>
                <FontAwesomeIcon
                    icon={faBoxOpen}
                    size='2x'
                    className='text-texts-secondary300'
                />
                <div className='font-semibold text-texts-secondary300'>
                    No Data
                </div>
            </div>
        );
    }
};

const DropDownButton = ({
    buttonTitle,
    disabled,
    buttonClassName,
    buttonHoverText,
    showButtonText,
    icon,
    bgColor,
    buttonStr,
    showDropdown,
    additionalButtonAction,
    customClass,
}) => {
    const wrapperClassName = buttonClassName?.includes('w-full')
        ? 'flex items-center justify-center w-full'
        : 'flex items-center justify-center w-fit';

    const getButtonClasses = () => {
        const classes = [
            'font-semibold',
            'flex',
            'justify-center',
            'items-center',
            'px-3',
            'py-1.5',
            'border',
            'shadow-inner',
            'outline-none',
            'focus:outline-backgrounds-primary200',
        ];

        // Disabled state
        if (disabled) {
            classes.push(
                'opacity-50',
                'cursor-not-allowed',
                'text-texts-secondary',
                'bg-backgrounds-white'
            );
        } else if (bgColor === 'white') {
            classes.push(
                'text-texts-tertiary',
                'bg-backgrounds-white',
                'hover:bg-backgrounds-primary100',
                'hover:border-backgrounds-primary200',
                'cursor-pointer'
            );
        } else {
            classes.push(
                'text-texts-tertiary',
                'bg-backgrounds-secondary',
                'hover:bg-backgrounds-secondary',
                'hover:border-backgrounds-secondary',
                'cursor-pointer'
            );
        }

        if (buttonClassName) {
            classes.push(buttonClassName);
        }

        return classes.join(' ');
    };

    // Style for button border radius
    const getButtonRadiusStyle = () => {
        if (buttonTitle) {
            return {
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                borderTopRightRadius: '9999px',
                borderBottomRightRadius: '9999px',
            };
        } else {
            return {
                borderRadius: '9999px',
            };
        }
    };

    // Style for title (left) segment
    const titleSegmentStyle = {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        borderTopLeftRadius: '9999px',
        borderBottomLeftRadius: '9999px',
    };

    return (
        <div
            className={wrapperClassName}
            onClick={() => {
                if (additionalButtonAction) additionalButtonAction();
            }}
        >
            {buttonTitle && (
                <div
                    className='w-max text-texts-secondary300 font-semibold shadow-inner px-2 py-1.5 border bg-backgrounds-primary100 text-base'
                    style={titleSegmentStyle}
                >
                    {buttonTitle}
                </div>
            )}
            <button
                type='button'
                className={customClass ? buttonClassName : getButtonClasses()}
                style={{
                    transition: 'all .15s ease',
                    ...getButtonRadiusStyle(),
                }}
                disabled={disabled}
                title={buttonHoverText || buttonStr || 'More options'}
                aria-haspopup='true'
                aria-expanded={showDropdown}
            >
                <div
                    className='w-full flex justify-between items-center select-none'
                    draggable='false'
                >
                    {showButtonText && buttonStr && (
                        <div className='flex-1 min-w-0 truncate mr-2 text-base'>
                            {buttonStr}
                        </div>
                    )}
                    {icon ? (
                        React.isValidElement(icon) ? (
                            icon
                        ) : (
                            <FontAwesomeIcon icon={icon} />
                        )
                    ) : (
                        <FontAwesomeIcon
                            icon={showDropdown ? faCaretUp : faCaretDown}
                        />
                    )}
                </div>
            </button>
        </div>
    );
};

export const Dropdown = ({
    dropDownList,
    showButtonText,
    buttonText,
    buttonTitle,
    buttonHoverText,
    additionalButtonAction,
    icon,
    listTitleText,
    buttonClassName,
    listClassName,
    disabled,
    placement,
    closeSignal,
    customClass,
    showCloseButton,
    listType,
    triggerComponent,
    getPopupContainer,
}) => {
    const [showDropdown, setShowDropdown] = useState(false);

    useEffect(() => {
        if (closeSignal) {
            setShowDropdown(false);
        }
    }, [closeSignal]);

    const handleOpenChange = (newOpenState) => {
        if (disabled) {
            setShowDropdown(false);
            return;
        }
        if (closeSignal && newOpenState) {
            setShowDropdown(false);
            return;
        }
        setShowDropdown(newOpenState);
    };

    const title = listTitleText ? listTitleText : 'More Options';
    const popoverTitle = (
        <Title
            title={title}
            handleOpenChange={handleOpenChange}
            showCloseButton={showCloseButton}
        />
    );

    const popoverContent = (
        <Content
            disabled={disabled}
            dropDownList={dropDownList}
            handleOpenChange={handleOpenChange}
            listType={listType}
            listClassName={listClassName}
        />
    );

    return (
        <Popover
            content={popoverContent}
            title={popoverTitle}
            trigger='click'
            placement={placement ? placement : 'bottomRight'}
            open={showDropdown}
            onOpenChange={handleOpenChange}
            {...(getPopupContainer ? { getPopupContainer } : {})}
        >
            {triggerComponent ? (
                React.cloneElement(triggerComponent, {
                    'aria-haspopup': 'true',
                    'aria-expanded': showDropdown,
                })
            ) : (
                <div onClick={(e) => e.stopPropagation()}>
                    <DropDownButton
                        additionalButtonAction={additionalButtonAction}
                        buttonTitle={buttonTitle}
                        disabled={disabled}
                        buttonClassName={buttonClassName}
                        buttonHoverText={buttonHoverText}
                        showButtonText={showButtonText}
                        icon={icon}
                        bgColor={
                            buttonClassName?.includes('bg-')
                                ? undefined
                                : 'white'
                        }
                        buttonStr={buttonText}
                        showDropdown={showDropdown}
                        customClass={customClass}
                    />
                </div>
            )}
        </Popover>
    );
};
